import { apiClient } from './request';
import { create, fromJson, type JsonValue, toJson } from '@bufbuild/protobuf';
import { type Instance, InstanceSchema } from '@/proto/agentplatform/v1/instance_pb';
import { type ChannelAccount } from '@/proto/agentplatform/v1/channel_pb';
import {
  CreateInstanceRequestSchema,
  CreateInstanceResponseSchema,
  GetInstanceRequestSchema,
  GetInstanceResponseSchema,
  UpdateInstanceRequestSchema,
  UpdateInstanceResponseSchema,
  DeleteInstanceRequestSchema,
  DeleteInstanceResponseSchema,
  ListInstancesResponseSchema,
  StopInstanceRequestSchema,
  StopInstanceResponseSchema,
  ListInstanceChannelAccountsRequestSchema,
  ListInstanceChannelAccountsResponseSchema,
} from '@/proto/agentplatform/httpv2/agent_server_pb';
import { type ListRequest, ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';

/** 列表返回的通用形状，兼容页面既有的 `data.data` / `data.total` 读法。 */
export interface InstanceListResult {
  data: Instance[];
  total: number;
}

export interface ChannelAccountListResult {
  data: ChannelAccount[];
  total: number;
}

/**
 * Instance（原 Application）API，内部对接后端 v2 接口，
 * 保持导出方法名与返回形状稳定，尽量减少页面改动。
 */
export const applicationApi = {
  list: async (params: ListRequest): Promise<InstanceListResult> => {
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/ListInstances',
      toJson(ListRequestSchema, params)
    );
    const resp = fromJson(ListInstancesResponseSchema, responseObject.data);
    return { data: resp.data?.items ?? [], total: resp.data?.total ?? 0 };
  },

  get: async (id: string): Promise<Instance> => {
    const req = create(GetInstanceRequestSchema, { id });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/GetInstance',
      toJson(GetInstanceRequestSchema, req)
    );
    const resp = fromJson(GetInstanceResponseSchema, responseObject.data);
    return resp.data ?? create(InstanceSchema, {});
  },

  create: async (data: Instance): Promise<Instance> => {
    const req = create(CreateInstanceRequestSchema, {
      agentId: data.agentId,
      name: data.name,
      description: data.description,
      params: data.params,
      subresources: data.subresources,
    });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/CreateInstance',
      toJson(CreateInstanceRequestSchema, req)
    );
    const resp = fromJson(CreateInstanceResponseSchema, responseObject.data);
    return resp.data ?? create(InstanceSchema, {});
  },

  update: async (id: string, data: Instance): Promise<Instance> => {
    const req = create(UpdateInstanceRequestSchema, {
      id,
      name: data.name,
      description: data.description,
      params: data.params,
    });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/UpdateInstance',
      toJson(UpdateInstanceRequestSchema, req)
    );
    const resp = fromJson(UpdateInstanceResponseSchema, responseObject.data);
    return resp.data ?? create(InstanceSchema, {});
  },

  delete: async (id: string): Promise<void> => {
    const req = create(DeleteInstanceRequestSchema, { id });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/DeleteInstance',
      toJson(DeleteInstanceRequestSchema, req)
    );
    fromJson(DeleteInstanceResponseSchema, responseObject.data);
  },

  stop: async (id: string): Promise<void> => {
    const req = create(StopInstanceRequestSchema, { id });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/StopInstance',
      toJson(StopInstanceRequestSchema, req)
    );
    fromJson(StopInstanceResponseSchema, responseObject.data);
  },

  listChannelAccounts: async (
    id: string,
    page = 1,
    pageSize = 100
  ): Promise<ChannelAccountListResult> => {
    const req = create(ListInstanceChannelAccountsRequestSchema, { id, page, pageSize });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/ListInstanceChannelAccounts',
      toJson(ListInstanceChannelAccountsRequestSchema, req)
    );
    const resp = fromJson(ListInstanceChannelAccountsResponseSchema, responseObject.data);
    return { data: resp.data?.items ?? [], total: resp.data?.total ?? 0 };
  },
};
